import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { convertOctToAptos } from '@pkg/utils';

import { type GeneralSearchResults, getChainFromIndex } from '@app/client';

import {
    CollectibleItem,
    CollectionItem,
    FreeYatItem,
    PodItem,
    SearchSectionTitle,
    UserItem,
    WishlistItem,
} from './components';

import { StyledSearchResultWrapper, StyledSearchSectionSpacer } from './styles';

interface GeneralSearchResultProps {
    loading?: boolean;
    results?: GeneralSearchResults;
    activeIndex?: number;
    onResultClicked?: VoidFunction;
    onFocusedElement?: (link: string) => void;
    query?: string;
}

function getPodSlug(name: string) {
    const reg = /[^a-zA-Z0-9]+/g;
    const slugged = name.replace(reg, '-').toLowerCase();
    return slugged.replace(/(^-)|(-+$)/g, '');
}

type SearchCategory = keyof GeneralSearchResults;

function isFocused(
    globalIndex: number | undefined,
    mapIndex: number,
    data: GeneralSearchResults | undefined,
    category: SearchCategory,
): boolean {
    if (!data || globalIndex === undefined) return false;
    const { collections, collectibles, pods, users, freeYats } = data;
    let length = mapIndex;
    if (category === 'collections') return globalIndex === length;
    length += collections.length;
    if (category === 'collectibles') return globalIndex === length;
    length += collectibles.length;
    if (category === 'users') return globalIndex === length;
    length += users.length;
    if (category === 'freeYats') return globalIndex === length;
    length += freeYats.length;
    if (category === 'pods') return globalIndex === length;
    length += pods.length;
    if (category === 'wishlists') return globalIndex === length;
    return false;
}

export const GeneralSearchResult = ({
    onResultClicked,
    onFocusedElement,
    loading,
    results,
    activeIndex,
    query,
}: GeneralSearchResultProps) => {
    const { t: translate } = useTranslation(['global_search']);
    const { collectibles, collections: _collections, users, freeYats, wishlists, pods } = results || {};

    const collections =
        _collections?.map((collection) => ({
            ...collection,
            chain: getChainFromIndex(collection.chain),
            floor_price:
                collection.chain === 2
                    ? convertOctToAptos(Number(collection.floor_price), 2).toString()
                    : collection.floor_price,
        })) || [];

    const [noResults, setNoResults] = useState(false);

    useEffect(() => {
        results && setNoResults(!loading && Object.values(results).every((value) => value.length === 0));
    }, [loading, results]);

    if (noResults && query !== '') {
        return (
            <StyledSearchResultWrapper>
                {!loading && <SearchSectionTitle>{translate('no_results')}</SearchSectionTitle>}
            </StyledSearchResultWrapper>
        );
    }

    const collectionResultMarkup = collections && collections.length > 0 && (
        <>
            <SearchSectionTitle>{translate('collections')}</SearchSectionTitle>
            {collections.map((collectionData, index) => (
                <CollectionItem
                    onClick={onResultClicked}
                    key={collectionData.id}
                    chain={collectionData.chain}
                    name={collectionData.name}
                    thumbnail={
                        collectionData.key_image ||
                        `https://daas.y.at/v2/metadata/image/collection/${collectionData.chain.toLowerCase()}.${
                            collectionData.slug || collectionData.id
                        }`
                    }
                    address={collectionData.address}
                    slug={collectionData.slug || collectionData.id}
                    floor_price={collectionData.floor_price}
                    focused={isFocused(activeIndex, index, results, 'collections')}
                    onFocused={onFocusedElement}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );

    const userResultMarkup = users && users.length > 0 && (
        <>
            <SearchSectionTitle>{translate('users')}</SearchSectionTitle>
            {users.map((userData, index) => (
                <UserItem
                    key={userData.id}
                    primaryYat={userData.primary_yat}
                    twitter={userData.twitter}
                    focused={isFocused(activeIndex, index, results, 'users')}
                    onFocused={onFocusedElement}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );
    const collectibleResultMarkup = collectibles && collectibles.length > 0 && (
        <>
            <SearchSectionTitle>{translate('collectibles')}</SearchSectionTitle>
            {collectibles.map((collectibleData, index) => (
                <CollectibleItem
                    onClick={onResultClicked}
                    key={collectibleData.name}
                    name={collectibleData.name}
                    thumbnail={collectibleData.image_url}
                    id={collectibleData.id ?? '1'}
                    focused={isFocused(activeIndex, index, results, 'collectibles')}
                    onFocused={onFocusedElement}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );
    const freeYatResultMarkup = freeYats && freeYats.length > 0 && (
        <>
            <SearchSectionTitle>{translate('free_yats')}</SearchSectionTitle>
            {freeYats.map((freeYat, index) => (
                <FreeYatItem
                    onClick={onResultClicked}
                    key={freeYat.id}
                    yat={freeYat.primary_yat}
                    focused={isFocused(activeIndex, index, results, 'freeYats')}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );
    const wishlistResultMarkup = wishlists && wishlists.length > 0 && (
        <>
            <SearchSectionTitle>{translate('wishlists')}</SearchSectionTitle>
            {wishlists.map((wishlist, index) => (
                <WishlistItem
                    onClick={onResultClicked}
                    key={wishlist.id}
                    id={wishlist.id}
                    name={wishlist.name}
                    thumbnails={wishlist.images}
                    focused={isFocused(activeIndex, index, results, 'wishlists')}
                    onFocused={onFocusedElement}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );
    const podResultMarkup = pods && pods.length > 0 && (
        <>
            <SearchSectionTitle>{translate('pods')}</SearchSectionTitle>
            {pods.map((pod, index) => (
                <PodItem
                    onClick={onResultClicked}
                    key={pod.id}
                    name={pod.name}
                    thumbnail={pod.header_image}
                    isPrivate={pod.isPrivate}
                    type={pod.isOwner ? 'owner' : pod.isMember ? 'member' : undefined}
                    slug={pod.slug ?? getPodSlug(pod.name)}
                    focused={isFocused(activeIndex, index, results, 'pods')}
                    onFocused={onFocusedElement}
                />
            ))}
            <StyledSearchSectionSpacer />
        </>
    );

    return (
        <StyledSearchResultWrapper>
            {collectionResultMarkup}
            {collectibleResultMarkup}
            {userResultMarkup}
            {freeYatResultMarkup}
            {podResultMarkup}
            {wishlistResultMarkup}
        </StyledSearchResultWrapper>
    );
};
